import {
  AppTable,
  generateQueryString,
  IAppTableColumn,
  IHttpResponse,
  PAGE_SIZE_OPTIONS,
  useFetchList,
} from '@qlibs/react-components';
import RowFilter from '@qlibs/react-components/dist/Table/RowFilter';
import { httpRequest } from '../../../helpers/api';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import useMasterData from '../../../hooks/useMasterData';
import useGroup from '../../../zustand/useGroup';
import { ReportEmailLogProperties } from '../types/report.type';
import LinkTable from '../../../components/LinkTable';
import { Modal, Spin, Tag, Tooltip } from 'antd';
import {
  EFormatCurrentDate,
  formatCurrentDate,
} from '../../../helpers/formatCurrentDate';
import { getErrorMessage, getErrorStatus } from '../../../helpers/httpError';
import usePermission from '../../../hooks/usePermission';
import dayjs from 'dayjs';
import { CompanyProperties } from '../../company/types/company.type';
import { AssetProperties, initialAsset } from '../../asset/types/asset.type';
import { CapturePdfParams } from '../../report/types/report-log.type';
import { InfoCircleFilled } from '@ant-design/icons';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { RiEqualizer2Line } from '@remixicon/react';
import {
  MasterReportMobileEmptyState,
  MasterReportMobileFilterActions,
  MasterReportMobileFilterApplyButton,
  MasterReportMobileFilterButton,
  MasterReportMobileFilterDrawer,
  MasterReportMobileFilterField,
  MasterReportMobileFilterFields,
  MasterReportMobileFilterForm,
  MasterReportMobileFilterRow,
  MasterReportMobileFilterSelect,
  MasterReportMobileMoreButton,
  MasterReportMobileMoreIcon,
  MasterReportMobilePageButton,
  MasterReportMobilePagination,
  MasterReportMobilePaginationActions,
  MasterReportMobilePaginationInfo,
  MasterReportMobileSearch,
  MasterReportMobileStackCell,
  MasterReportMobileStatus,
  MasterReportMobileTable,
  MasterReportMobileTableWrap,
} from '../mobile';
import { Dropdown, MenuProps } from 'antd';
import {
  DEFAULT_REPORT_DAY_OFFSET,
  formatReportSendingTime,
  getEmailLogScheduleRange,
  getReportTimezoneOffset,
  ReportScheduleMetadata,
} from '../helpers/emailSchedule';
import {
  clearPdfDownloadCooldown,
  formatPdfDownloadCooldown,
  getPdfDownloadCooldownRemaining,
  isPdfDownloadCooldownRange,
  startPdfDownloadCooldown,
} from '../../../helpers/pdfDownloadCooldown';
import useReportDownload from '../../../zustand/useReportDownload';

const FEATURE = 'MASTER_REPORT';
const dateFormat = 'DD MMM YYYY HH-mm';
const DEFAULT_NO_DATA_DOWNLOAD_MESSAGE =
  'No report data for the selected filter. PDF download was canceled.';
const DEFAULT_NO_DATA_RESEND_MESSAGE =
  'No report data for the selected filter. Email delivery was canceled.';

const isNoDataMessage = (value: string) => {
  const normalizedValue = value.toLowerCase();
  return (
    normalizedValue.includes('no data') ||
    normalizedValue.includes('data report kosong') ||
    normalizedValue.includes('tidak ada data') ||
    normalizedValue.includes('data completion')
  );
};

const normalizeNoDataMessage = (
  content: string | undefined,
  fallback: string,
) => (content && !isNoDataMessage(content) ? content : fallback);

const getMetaTarget = (record?: Partial<ReportEmailLogProperties>) =>
  record?.metaTarget && typeof record.metaTarget === 'object'
    ? record.metaTarget
    : {};

const getCompanyName = (record?: Partial<ReportEmailLogProperties>) =>
  getMetaTarget(record).company?.name || record?.companyName || '-';

const getAssetType = (record?: Partial<ReportEmailLogProperties>) =>
  getMetaTarget(record).asset?.assetType ||
  getMetaTarget(record).assetType ||
  '';

type EmailLogProps = {
  isMobile?: boolean;
};

const EmailLog = forwardRef<any, EmailLogProps>((props, ref) => {
  const { isMobile = false } = props;
  const { company, asset, setAsset, setCompany } = useGroup();
  const {
    companies,
    getListCompanies,
    assets,
    getListAssets,
    loadingAsset,
    setAssets,
  } = useMasterData();
  const { isUserHasPermission } = usePermission();
  const [loadingResend, setLoadingResend] = useState<{
    value: boolean;
    id?: string;
  }>({
    value: false,
    id: undefined,
  });
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const { activeDownload, startDownload, finishDownload } = useReportDownload();
  const isDownloadBusy = activeDownload !== null;
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [mobileFilterValues, setMobileFilterValues] = useState({
    company: '',
    asset: '',
    status: 'All',
  });

  useEffect(() => {
    getListCompanies();
  }, []);

  useEffect(() => {
    if (company && company.companyId) {
      setAssets(company.assets ?? []);
      setAsset({ ...initialAsset });
    }
  }, [company]);

  const filterCompanyOptions = React.useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [{ value: '', label: 'All' }, ...data];
  }, [companies]);

  const filterAssetOptions = React.useMemo(() => {
    const data = assets.map((item) => ({
      value: item.assetId,
      label: item.name,
    }));
    return [{ value: '', label: 'All' }, ...data];
  }, [assets]);

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
    fetchList,
    setIsLoading,
  } = useFetchList<ReportEmailLogProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'report-email-log',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterCompany: company?.companyId || '',
    },
    pageQuery: {
      defaultValue: {
        page: 1,
      },
    },
  });

  useImperativeHandle(ref, () => ({
    refetch: fetchList,
  }));

  const filterData: any = [
    {
      type: 'select',
      key: 'company',
      label: 'Company',
      options: filterCompanyOptions,
      value: pageQueries['filterCompanyId'] || 'All',
      onChange: async (value: any) => {
        filterDropdown({
          ['filterCompany']: value === 'All' ? '' : value,
        });
        const found = companies.find((item) => item.companyId === value);
        if (found) {
          setCompany(found);
        }
        await getListAssets({ filterCompanyIds: value });
      },
      colSpan: 4,
    },
    {
      type: 'select',
      key: 'asset',
      label: 'Asset',
      disabled: true,
      options: filterAssetOptions,
      value: pageQueries['filterAsset'] || 'All',
      onChange: (value: any) => {
        filterDropdown({
          ['filterAsset']: value === 'All' ? '' : value,
        });
      },
      colSpan: 4,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'All',
          label: 'All',
        },
        {
          value: 'sent',
          label: 'SENT',
        },
        {
          value: 'pending',
          label: 'PENDING',
        },
        {
          value: 'failed',
          label: 'FAILED',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          ['filterStatus']: value === 'All' ? '' : value,
        });
      },
      colSpan: 3,
    },
  ];

  const convertTimeRange = (time?: string) => {
    if (!time) {
      return '-';
    }

    return time.split(':').slice(0, 2).join(':');
  };

  const formatScheduleWindow = (metaTarget: ReportScheduleMetadata) =>
    `H-${metaTarget.timeFromDayOffset ?? DEFAULT_REPORT_DAY_OFFSET} ${convertTimeRange(
      metaTarget.timeFrom,
    )} - H-${metaTarget.timeToDayOffset ?? DEFAULT_REPORT_DAY_OFFSET} ${convertTimeRange(
      metaTarget.timeTo,
    )} (GMT+${getReportTimezoneOffset(metaTarget.timezoneOffset)})`;

  const columnsEmailTargetAsset: IAppTableColumn<ReportEmailLogProperties>[] = [
    {
      title: 'ASSET',
      dataIndex: 'assetName',
      key: 'assetName',
      width: 200,
      render: (value, records) => {
        const companyName = getCompanyName(records);

        return (
          <div>
            <div style={{ opacity: 0.6, fontSize: 12 }}>Company:</div>
            <div style={{ marginBottom: 4 }}>{companyName}</div>
            <div style={{ opacity: 0.6, fontSize: 12, marginTop: 4 }}>
              Asset:
            </div>
            <div>{value}</div>
          </div>
        );
      },
    },
    {
      title: 'RUN AT',
      dataIndex: 'sendingScheduleDate',
      key: 'sendingScheduleDate',
      render: (value, records) => {
        const metaTarget = getMetaTarget(records);

        return (
          <div>
            {formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH)}
            {'  '}
            {!metaTarget.cronJobName && (
              <Tooltip title="Send manually from custom send email">
                <InfoCircleFilled style={{ color: '#1677ff' }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'sendingStatus',
      key: 'sendingStatus',
      render: (value, _) => {
        if (value === 'pending') {
          return <Tag color="warning">{value.toUpperCase()}</Tag>;
        } else if (value === 'failed') {
          return <Tag color="error">{value.toUpperCase()}</Tag>;
        } else if (value === 'sent') {
          return <Tag color="success">{value.toUpperCase()}</Tag>;
        } else {
          return <span>-</span>;
        }
      },
    },
    {
      title: 'TEMPLATE TYPE',
      dataIndex: 'templateType',
      key: 'templateType',
      render: (value) => {
        if (value === 'operation_mode') {
          return <Tag color="blue">Operation Mode</Tag>;
        }

        return <Tag>Daily Report</Tag>;
      },
    },
    {
      title: 'REMARK',
      dataIndex: 'remark',
      key: 'remark',
    },
    {
      title: 'RECEIVERS',
      dataIndex: 'receivers',
      key: 'receivers',
      render: (value, _) => (
        <>
          {value.length > 2 ? (
            <Tooltip title={value.map((item: any) => item.email).join(', ')}>
              {value.slice(0, 2).map((item: any, index: number) => (
                <div key={index}>{item.email}</div>
              ))}
              <div>+{value.length - 2} more</div>
            </Tooltip>
          ) : (
            value.map((item: any, index: number) => (
              <div key={index}>{item.email}</div>
            ))
          )}
        </>
      ),
    },
    {
      title: 'SCHEDULE',
      dataIndex: 'sendingScheduleDate',
      key: 'sendingScheduleDate',
      render: (_, records) => {
        const metaTarget = getMetaTarget(records);

        return (
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Run every:</div>
            <div>
              {metaTarget.frequency === 'DAILY'
                ? `Every day at ${formatReportSendingTime(
                    metaTarget.sendingAt,
                    metaTarget.timezoneOffset,
                  )}`
                : formatCurrentDate(
                    metaTarget.sendingAt,
                    EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                  )}
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
              Data Time Range:
            </div>
            <div>
              {formatScheduleWindow(metaTarget)}
            </div>
          </div>
        );
      },
    },
    {
      title: 'SENDING COUNT',
      dataIndex: 'sendingCount',
      key: 'sendingCount',
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      type: 'actions',
      actions: [
        isUserHasPermission([FEATURE + '.UPDATE'])
          ? {
              key: 'resend',
              label: 'Resend',
              onPress: (id) => {
                Modal.confirm({
                  title: 'Are you sure want to resend this report?',
                  content:
                    'This action will resend this report based on this date and will be send to all receivers',
                  onOk: () => {
                    id && resendEmail(id);
                  },
                });
              },
            }
          : undefined,
        isUserHasPermission([FEATURE + '.DETAIL'])
          ? {
              key: 'download',
              label: 'Download',
              onPress: (id) => {
                id && handleDownloadPdf(id);
              },
              disabled: () => isDownloadBusy,
            }
          : undefined,
      ],
    },
  ];

  const convertDeviceType = (deviceIds: string) => {
    let res: string[] = [];
    // ae-3,flowmeter,rpm,ae-1,ae-2,gps,gateway,operation_mode
    const devices = deviceIds.split(',');
    devices.forEach((item) => {
      if (item.includes('gps')) {
        res.push('gps');
      }
      if (item.includes('rpm')) {
        res.push('rpm');
      }
      if (item.includes('fm')) {
        res.push('flowmeter');
      }
      if (item.includes('ae-1')) {
        res.push('ae-1');
      }
      if (item.includes('ae-2')) {
        res.push('ae-2');
      }
      if (item.includes('ae-3')) {
        res.push('ae-3');
      }
      if (item.includes('gateway')) {
        res.push('gateway');
      }
    });

    return res.join(',');
  };

  async function handleDownloadPdf(id: string) {
    if (loadingDownload || isDownloadBusy) {
      return;
    }

    const found = data.find((item) => item.id === Number(id));
    if (found) {
      const metaTarget = getMetaTarget(found);
      const assetType = getAssetType(found);
      const sendingScheduleDate =
        metaTarget.sendingScheduleDate || found.sendingScheduleDate;

      if (!sendingScheduleDate || !metaTarget.timeFrom || !metaTarget.timeTo) {
        Modal.warning({
          title: 'Download Failed',
          content:
            'This email log is missing schedule metadata. Please resend the report first.',
          okText: 'OK',
        });
        return;
      }

      if (!assetType) {
        Modal.warning({
          title: 'Download Failed',
          content:
            'This email log is missing asset metadata. Please resend the report first.',
          okText: 'OK',
        });
        return;
      }

      let deviceType = convertDeviceType(found.deviceIds);
      const reportRange = getEmailLogScheduleRange(
        metaTarget,
        sendingScheduleDate,
      );
      const timezone = getReportTimezoneOffset(metaTarget.timezoneOffset);

      const params = {
        startAt: reportRange.startAt,
        endAt: reportRange.endAt,
        assetId: found.assetId,
        assetName: found.assetName,
        deviceType,
        interval: metaTarget.interval || 'hour',
        timezone,
      };

      const cooldownRemaining = getPdfDownloadCooldownRemaining();
      if (
        isPdfDownloadCooldownRange(params.startAt, params.endAt) &&
        cooldownRemaining > 0
      ) {
        Modal.warning({
          title: 'Download Limited',
          content: `Reports longer than 5 days can be downloaded again in ${formatPdfDownloadCooldown(
            cooldownRemaining,
          )}.`,
          okText: 'OK',
        });
        return;
      }

      onDownloadPDF(params, assetType, found.deviceIds);
    }
  }

  async function onDownloadPDF(
    params: {
      startAt: number;
      endAt: number;
      assetId: string;
      assetName: string;
      deviceType: string;
      interval: string;
      timezone: number;
    },
    assetType: string,
    deviceIds: string,
  ) {
    const isCooldownRange = isPdfDownloadCooldownRange(
      params.startAt,
      params.endAt,
    );

    if (!startDownload('pdf')) {
      return;
    }

    try {
      setLoadingDownload(true);
      if (isCooldownRange) {
        startPdfDownloadCooldown();
      }
      let newFilter = {
        ...params,
      };
      let dataDownloaded = ['summary', 'ae_fuel_consumption'];

      const resOp = await httpRequest.get<IHttpResponse<{ isExist: boolean }>>(
        `/operation-mode/${params.assetId}/check-by-asset`,
      );

      if (resOp.data.payload && resOp.data.payload.isExist) {
        newFilter = {
          ...newFilter,
          deviceType: newFilter.deviceType.concat(',operation_mode'),
        };
        dataDownloaded.push('operation_mode');
      }

      if (deviceIds.includes('gps')) {
        dataDownloaded.push('vessel_tracking');
      }
      if (assetType === 'fms') {
        const devices = deviceIds.split(',');

        dataDownloaded.push('rpm_fuel_speed_ae');
        dataDownloaded.push('me_fuel_consumption');

        devices.forEach((item) => {
          if (item.includes('fm-1')) {
            dataDownloaded.push('port_fuel_consumption_running');
          }
          if (item.includes('fm-2')) {
            dataDownloaded.push('starboard_fuel_consumption_running');
          }
          if (item.includes('fm-3')) {
            dataDownloaded.push('center_fuel_consumption_running');
          }
        });
      } else if (assetType === 'ems') {
        dataDownloaded.push('rpm_speed_ae');
      }

      const query: CapturePdfParams = {
        ...newFilter,
        interval: newFilter.interval || params.interval,
        scheduleReport: 1,
        dataDownloaded,
      };

      const res: any = await httpRequest.get(
        `/report-log/generate-report-pdf${generateQueryString(query)}`,
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const startAt = dayjs(params.startAt * 1000)
        .utcOffset(params.timezone * 60)
        .format(dateFormat);
      const endAt = dayjs(params.endAt * 1000)
        .utcOffset(params.timezone * 60)
        .format(dateFormat);
      const fileName = `Report ${params.assetName} ${startAt} ${endAt}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('failed generate report', error);
      // Failed attempts should not lock the user out for the full cooldown.
      if (isCooldownRange) {
        clearPdfDownloadCooldown();
      }

      const statusCode = getErrorStatus(error);
      const errorMessage = await getErrorMessage(error);

      if (statusCode === 400) {
        Modal.warning({
          title: 'No Data',
          content: normalizeNoDataMessage(
            errorMessage,
            DEFAULT_NO_DATA_DOWNLOAD_MESSAGE,
          ),
          okText: 'OK',
        });
        return;
      }

      Modal.error({
        title: 'Download Failed',
        content:
          errorMessage || 'Failed to generate report PDF. Please try again.',
        okText: 'OK',
      });
    } finally {
      setLoadingDownload(false);
      finishDownload();
    }
  }

  const resendEmail = async (id: string) => {
    try {
      setLoadingResend({
        value: true,
        id,
      });
      const dataLog = data.find((item) => item.id === Number(id));

      if (dataLog) {
        const metaTarget = getMetaTarget(dataLog);
        const sendingScheduleDate =
          metaTarget.sendingScheduleDate || dataLog.sendingScheduleDate;
        const receiverEmail = Array.isArray(dataLog.receivers)
          ? dataLog.receivers
              .map((item: any) => item.email)
              .filter(Boolean)
              .join(',')
          : '';

        if (
          !sendingScheduleDate ||
          !metaTarget.timeFrom ||
          !metaTarget.timeTo ||
          !receiverEmail
        ) {
          Modal.warning({
            title: 'Resend Failed',
            content:
              'This email log is missing resend metadata. Please send a new custom email.',
            okText: 'OK',
          });
          return;
        }

        const reportRange = getEmailLogScheduleRange(
          metaTarget,
          sendingScheduleDate,
        );

        await httpRequest.post('/scheduler/send-to-email', {
          assetId: dataLog.assetId,
          startAt: reportRange.startAt,
          endAt: reportRange.endAt,
          receiverEmail,
          interval: metaTarget.interval || 'hour',
          timezone: getReportTimezoneOffset(metaTarget.timezoneOffset),
          logId: id,
        });
      }

      await fetchList();
    } catch (error) {
      console.error('Failed resend email report', error);

      const statusCode = getErrorStatus(error);
      const errorMessage = await getErrorMessage(error);
      const normalizedMessage = (errorMessage || '').toLowerCase();

      if (
        statusCode === 400 ||
        normalizedMessage.includes('no data') ||
        normalizedMessage.includes('data completion')
      ) {
        Modal.warning({
          title: 'No Data',
          content: normalizeNoDataMessage(
            errorMessage,
            DEFAULT_NO_DATA_RESEND_MESSAGE,
          ),
          okText: 'OK',
        });
        return;
      }

      Modal.error({
        title: 'Send Failed',
        content:
          errorMessage || 'Failed resend email report. Please try again.',
        okText: 'OK',
      });
    } finally {
      setLoadingResend({
        value: false,
        id: undefined,
      });
    }
  };

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      company: pageQueries.filterCompany || pageQueries.filterCompanyId || '',
      asset: pageQueries.filterAsset || '',
      status: pageQueries.filterStatus || 'All',
    });
  }, [isMobileFilterOpen, pageQueries]);

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  const handleApplyMobileFilter = async () => {
    filterDropdown({
      filterCompany: mobileFilterValues.company,
      filterAsset: mobileFilterValues.asset,
      filterStatus:
        mobileFilterValues.status === 'All' ? '' : mobileFilterValues.status,
    });

    const found = companies.find(
      (item) => item.companyId === mobileFilterValues.company,
    );

    if (found) {
      setCompany(found);
      await getListAssets({ filterCompanyIds: found.companyId });
    }

    setIsMobileFilterOpen(false);
  };

  const getMobileActionItems = (
    record: ReportEmailLogProperties,
  ): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (isUserHasPermission([FEATURE + '.UPDATE'])) {
      items.push({ key: 'resend', label: 'Resend' });
    }

    if (isUserHasPermission([FEATURE + '.DETAIL'])) {
      items.push({
        key: 'download',
        label: 'Download',
        disabled: isDownloadBusy,
      });
    }

    return items;
  };

  const handleMobileActionClick =
    (record: ReportEmailLogProperties) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      const id = String(record.id);

      if (info?.key === 'resend') {
        Modal.confirm({
          title: 'Are you sure want to resend this report?',
          content:
            'This action will resend this report based on this date and will be send to all receivers',
          onOk: () => resendEmail(id),
        });
      }

      if (info?.key === 'download') {
        handleDownloadPdf(id);
      }
    };

  const renderMobileActionCell = (record: ReportEmailLogProperties) => {
    const items = getMobileActionItems(record);

    if (!items?.length) {
      return null;
    }

    return (
      <Dropdown
        trigger={['click']}
        menu={{
          items,
          onClick: handleMobileActionClick(record),
        }}
      >
        <MasterReportMobileMoreButton
          type="button"
          aria-label="Open email log action"
        >
          <MasterReportMobileMoreIcon />
        </MasterReportMobileMoreButton>
      </Dropdown>
    );
  };

  const renderMobileReceivers = (value: any) => {
    if (!Array.isArray(value) || !value.length) {
      return '-';
    }

    return value
      .slice(0, 2)
      .map((item: any) => item.email)
      .filter(Boolean)
      .join(', ');
  };

  if (isMobile) {
    return (
      <>
        <MasterReportMobileFilterRow>
          <MasterReportMobileSearch
            allowClear
            defaultValue={pageQueries.search}
            placeholder="Search by company"
            suffix={<SearchOutlined style={{ color: '#9facbf' }} />}
            onChange={(event) => handleSearch(event.target.value)}
          />
          <MasterReportMobileFilterButton
            type="button"
            aria-label="Open email log filters"
            onClick={() => setIsMobileFilterOpen(true)}
          >
            <RiEqualizer2Line size={20} />
          </MasterReportMobileFilterButton>
        </MasterReportMobileFilterRow>

        <Spin spinning={loadingResend.value || loadingDownload}>
          {isLoading ? (
            <MasterReportMobileEmptyState>
              Loading email logs...
            </MasterReportMobileEmptyState>
          ) : !data.length ? (
            <MasterReportMobileEmptyState>
              No email log data found.
            </MasterReportMobileEmptyState>
          ) : (
            <MasterReportMobileTableWrap>
              <MasterReportMobileTable $minWidth={1081}>
                <thead>
                  <tr>
                    <th style={{ width: 161 }}>Asset</th>
                    <th style={{ width: 86 }}>Run at</th>
                    <th style={{ width: 72 }}>Status</th>
                    <th style={{ width: 194 }}>Remark</th>
                    <th style={{ width: 117 }}>Receiver</th>
                    <th style={{ width: 114 }}>Schedule</th>
                    <th style={{ width: 120 }}>Sending Count</th>
                    <th style={{ width: 138 }}>Create at</th>
                    <th style={{ width: 79 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => {
                    const metaTarget = getMetaTarget(item);
                    const isSent = item.sendingStatus === 'sent';

                    return (
                      <tr key={item.id}>
                        <td>
                          <MasterReportMobileStackCell>
                            <span>Company :</span>
                            <strong>{getCompanyName(item)}</strong>
                            <span>Asset :</span>
                            <strong>{item.assetName || '-'}</strong>
                          </MasterReportMobileStackCell>
                        </td>
                        <td>
                          {formatCurrentDate(
                            item.sendingScheduleDate,
                            EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                          )}
                        </td>
                        <td>
                          <MasterReportMobileStatus $active={isSent}>
                            {item.sendingStatus || '-'}
                          </MasterReportMobileStatus>
                        </td>
                        <td>{item.remark || '-'}</td>
                        <td>{renderMobileReceivers(item.receivers)}</td>
                        <td>
                          <MasterReportMobileStackCell>
                            <span>Run every:</span>
                            <strong>
                              {metaTarget.frequency === 'DAILY'
                                ? `Every day at ${formatReportSendingTime(
                                    metaTarget.sendingAt,
                                    metaTarget.timezoneOffset,
                                  )}`
                                : formatCurrentDate(
                                    metaTarget.sendingAt,
                                    EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                                  )}
                            </strong>
                            <span>Data Time Range:</span>
                            <strong>{formatScheduleWindow(metaTarget)}</strong>
                          </MasterReportMobileStackCell>
                        </td>
                        <td>{item.sendingCount || 0}</td>
                        <td>
                          {formatCurrentDate(
                            item.createdAt,
                            EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                          )}
                        </td>
                        <td>{renderMobileActionCell(item)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </MasterReportMobileTable>
            </MasterReportMobileTableWrap>
          )}
        </Spin>

        <MasterReportMobilePagination>
          <MasterReportMobilePaginationInfo>
            Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
            results
          </MasterReportMobilePaginationInfo>
          <MasterReportMobilePaginationActions>
            <MasterReportMobilePageButton
              type="button"
              disabled={mobileCurrentPage <= 1}
              onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
              aria-label="Previous page"
            >
              <LeftOutlined />
            </MasterReportMobilePageButton>
            <MasterReportMobilePageButton type="button" $active>
              {mobileCurrentPage}
            </MasterReportMobilePageButton>
            <MasterReportMobilePageButton
              type="button"
              disabled={mobileCurrentPage >= mobileTotalPages}
              onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
              aria-label="Next page"
            >
              <RightOutlined />
            </MasterReportMobilePageButton>
          </MasterReportMobilePaginationActions>
        </MasterReportMobilePagination>

        <MasterReportMobileFilterDrawer
          title="Filter"
          placement="bottom"
          height="auto"
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <MasterReportMobileFilterForm>
            <MasterReportMobileFilterFields>
              <MasterReportMobileFilterField>
                Company
                <MasterReportMobileFilterSelect
                  value={mobileFilterValues.company}
                  options={filterCompanyOptions}
                  onChange={(value) => {
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      company: String(value),
                      asset: '',
                    }));

                    if (value) {
                      getListAssets({ filterCompanyIds: String(value) });
                    }
                  }}
                />
              </MasterReportMobileFilterField>
              <MasterReportMobileFilterField>
                Asset
                <MasterReportMobileFilterSelect
                  value={mobileFilterValues.asset}
                  options={filterAssetOptions}
                  disabled={!mobileFilterValues.company}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      asset: String(value),
                    }))
                  }
                />
              </MasterReportMobileFilterField>
              <MasterReportMobileFilterField>
                Status
                <MasterReportMobileFilterSelect
                  value={mobileFilterValues.status}
                  options={[
                    { value: 'All', label: 'All' },
                    { value: 'sent', label: 'SENT' },
                    { value: 'pending', label: 'PENDING' },
                    { value: 'failed', label: 'FAILED' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      status: String(value),
                    }))
                  }
                />
              </MasterReportMobileFilterField>
            </MasterReportMobileFilterFields>
            <MasterReportMobileFilterActions>
              <MasterReportMobileFilterApplyButton
                type="primary"
                onClick={handleApplyMobileFilter}
              >
                Apply
              </MasterReportMobileFilterApplyButton>
            </MasterReportMobileFilterActions>
          </MasterReportMobileFilterForm>
        </MasterReportMobileFilterDrawer>
      </>
    );
  }

  return (
    <div style={{ marginTop: 15 }}>
      <RowFilter
        filterValues={{
          status: pageQueries.filterStatus || 'All',
          filterData: pageQueries.filterData || 'default',
          company: company?.name || 'All',
          asset: asset?.name || 'All',
        }}
        filters={[filterData]}
      />
      <Spin spinning={loadingResend.value || loadingDownload}>
        <AppTable
          isLoading={isLoading}
          keyId="id"
          columns={columnsEmailTargetAsset}
          data={data}
          pagination={pagination}
          onChangePage={changePage}
          onChangeLimit={changeLimit}
        />
      </Spin>
    </div>
  );
});

export default EmailLog;
